import { format } from 'date-fns';

import {
  PRODUCT_LINK,
  QUERY_TYPE_ICONS,
  dataSourceIconMap,
  queryTypeIconMap,
} from '@discover-frontend/constants';
import { isGenericGb, isGenericUs } from '@discover-frontend/utils';

import { PSYCHOGRAPHIC_PREFIX } from 'src/constants/config';
import { Campaign } from 'src/modules/audience-activation/types';

type Item = {
  type?: string;
  access?: string;
  campaign?: Campaign;
  metadata?: { query_type: string; data_source: string };
};

export const isEmpty = (value: any) => {
  return (
    value === undefined ||
    value === null ||
    (Array.isArray(value) && value.length === 0) ||
    value === ''
  );
};

export const getItemIcon = (item: Item, productDataSources) => {
  const { type, access, metadata, campaign } = item;
  const dataSource = metadata?.data_source;
  const queryType = metadata?.query_type;
  const hasSources = !isEmpty(productDataSources);

  if (campaign) return QUERY_TYPE_ICONS.CAMPAIGN;

  if (type === 'folder') return QUERY_TYPE_ICONS.FOLDER;

  if (type === 'audience' && access === 'public')
    return QUERY_TYPE_ICONS.AUDIENCE;

  if (hasSources && dataSource && dataSourceIconMap[dataSource])
    return dataSourceIconMap[dataSource];

  if (queryType && queryTypeIconMap[queryType])
    return queryTypeIconMap[queryType];

  return 'heart';
};

export const createOnDropObj = (
  e: { dataTransfer: { getData: (s: string) => any } },
  parentIdTo: any
) => {
  const itemInfos = e.dataTransfer.getData('itemInfos')
    ? JSON.parse(e.dataTransfer.getData('itemInfos'))
    : null;
  return {
    itemId: e.dataTransfer.getData('itemId'),
    entityIndex: e.dataTransfer.getData('entityIndex'),
    parentIdFrom: e.dataTransfer.getData('parentId'),
    itemInfos,
    parentIdTo,
    isDemographic: e.dataTransfer.getData('isDemographic'),
    demoClass: e.dataTransfer.getData('demoClass'),
  };
};

export const getNotGroupOperator = (operator: string) => {
  const indexOpenBreaket = operator.indexOf('(');
  const indexCloseBreaket = operator.indexOf(')');
  const groupOperator = operator
    .slice(indexOpenBreaket + 1, indexCloseBreaket)
    .trim();

  return groupOperator;
};

/** UI `NOT (AND|OR)` or API `NOT` blocks used for exclusion groups in query preview. */
export const isNotExclusionGroup = (operator: string | undefined) =>
  operator === 'NOT (AND)' ||
  operator === 'NOT (OR)' ||
  operator === 'NOT';

export const getItemTypeToShow = (type: string) => {
  if (type === 'membership') {
    return 'conversation';
  }
  if (!type.includes('demographic')) {
    return type;
  }

  return 'demographic';
};

export const capitalizeString = (lower: string) =>
  lower.charAt(0).toUpperCase() + lower.substring(1);

export const alphabeticallySortByKey = <T extends any>(...keys: string[]) => {
  let sortOrder = 1;

  return (a: T, b: T) => {
    let i = 0;
    let result = 0;
    while (i < keys.length && result === 0) {
      let testKey = keys[i];
      if (keys[i][0] === '-') {
        sortOrder = -1;
        testKey = keys[i].substr(1);
      }

      result =
        sortOrder *
        (a[testKey].toString() < b[testKey].toString()
          ? -1
          : a[testKey].toString() > b[testKey].toString()
          ? 1
          : 0);
      i++;
    }
    return result;
  };
};

type Timeframe = {
  id?: string;
  start_week: number;
  end_week: number;
  label: string;
  type?: string;
};
export const getTimeframesOptions = (
  timeframes?: Timeframe[],
  selectedTimeframe?: string
) => {
  const getTimeFrameValue = (timeframe: Timeframe) => {
    if (!timeframe.id) {
      return timeframe.end_week + 1 - timeframe.start_week;
    }

    return timeframe.id;
  };

  if (!timeframes) {
    return [];
  }

  const timeframesOptions = timeframes.map((timeframe) => ({
    ...timeframe,
    isActive: selectedTimeframe === getTimeFrameValue(timeframe),
    label: timeframe.label,
    value: getTimeFrameValue(timeframe),
  }));

  if (!timeframes[0].type) {
    return timeframesOptions;
  }

  return timeframesOptions.sort(
    alphabeticallySortByKey('type', 'duration', 'label')
  );
};

export const getTimeframeLabelFromId = (
  id?: string,
  timeframes?: Timeframe[]
) => {
  if (!id || !timeframes) {
    return '-';
  }

  const selectedTimeframe = timeframes
    .flat()
    .find((timeframe) => timeframe.id === id);
  return (selectedTimeframe && selectedTimeframe.label) || '-';
};

export const isCustomTimeframe = (timeframe: string) => {
  const pattern = new RegExp(
    '([0-9]*\\-[0-9]*\\-[0-9]*)\\,([0-9]*\\-[0-9]*\\-[0-9]*)'
  );
  return (timeframe && pattern.test(timeframe)) || false;
};

export const hasChildren = (
  item: { id: string },
  flattedTaxonomy: { parent_id: string }[]
) => {
  const exist =
    flattedTaxonomy.findIndex((child) => item.id === child.parent_id) !== -1;
  return exist;
};

export const getProductLink = (product_slug: string) => {
  return PRODUCT_LINK[process.env.REACT_APP_API_ENV][product_slug];
};

type Metadata = {
  data_source?: string;
  base_query?: {
    whole_population: any;
    query_block: { arguments_list: { id: any; type?: string }[] };
  };
};
export const getBaseLabel = (
  metadata: Metadata,
  taxonomy: {
    baseTaxonomyWithLifestyles: {
      data_source: { label: string; id: string; type?: string }[];
    };
  }
) => {
  if (!metadata.base_query || metadata.base_query.whole_population) {
    return isGenericUs()
      ? 'US Population'
      : isGenericGb()
      ? 'GB Population 15+'
      : 'Global Population';
  }
  const baseQueryId = metadata.base_query.query_block.arguments_list[0].id;
  const baseQueryType = metadata.base_query.query_block.arguments_list[0].type;

  /* We have changed the structure of the taxonomy so we need to know the source we are looking at */
  const activeSource: string = metadata?.data_source;
  /* We make sure we have an array to check in case there is no data_source */
  let sourceTaxonomy = [];
  /* If we have a valid taxonomy list we will use it */
  if (activeSource && taxonomy.baseTaxonomyWithLifestyles[activeSource]) {
    sourceTaxonomy = taxonomy.baseTaxonomyWithLifestyles[activeSource];
  }

  const match = sourceTaxonomy.flat().find((item) => {
    const sameId = item.id === baseQueryId;
    const sameIdForPsycho = item.id === `${PSYCHOGRAPHIC_PREFIX}${baseQueryId}`;
    let sameType = true;
    if (baseQueryType) {
      sameType = (item.type || 'category') === baseQueryType;
    }
    return (sameId || sameIdForPsycho) && sameType;
  });
  return match ? match.label : '';
};

export const getQueryTypeLabelAndIcon = (queryType?: string) => {
  if (queryType) {
    if (queryType === 'interest') {
      return ['Interest', 'heart'];
    }
    if (queryType === 'conversation') {
      return ['Conversation', 'conversation'];
    }
    if (queryType === 'segmentation') {
      return ['Segment', 'segmentation'];
    }
    if (queryType === 'tsv') {
      return ['TSV', 'users'];
    }
    return ['Timed Interest', 'calendar'];
  }
  return [undefined, undefined];
};

export const getQueryDataSourceLabelAndIcon = (dataSource?: string) => {
  if (dataSource) {
    if (dataSource === 'twitter') {
      return ['Social', 'social'];
    }
    if (dataSource === 'measure_apps') {
      return ['App Usage', 'app'];
    }
    if (dataSource === 'measure_browsing') {
      return ['Web Browsing', 'web'];
    }
  }
  return [undefined, undefined];
};

export { debounce } from 'lodash';

export const getInitials = (email, surname, name) => {
  if (!email) {
    return '';
  }

  if (name && surname) {
    return `${name.charAt(0)}${surname.charAt(0)}`;
  }

  return `${email.substring(0, 2)}`;
};

export const getFullName = (surname, name) => {
  if (name && surname) {
    return `${name} ${surname}`;
  }

  return null;
};

export const getFormattedDate = (date, dateFormat = 'MM/DD/YYYY') => {
  const dateObject = new Date(date);
  const newDate = new Date(
    dateObject.valueOf() + dateObject.getTimezoneOffset() * 60 * 1000
  );
  return format(newDate, dateFormat);
};

const searchTreeQuery = (tree) => {
  if (!tree) {
    return [];
  }
  return tree.reduce(function (acc, o) {
    if (o.source && acc.indexOf(o.source) === -1) {
      acc.push(o.source);
    }
    if (o.arguments_list) acc = acc.concat(searchTreeQuery(o.arguments_list));
    return acc;
  }, []);
};

export const getAudienceBuildersIdsAndSourceIdsFromQuery = (
  query,
  fusions,
  audienceBuilders
) => {
  const metadata = query?.metadata;
  const sourcesWithDuplicates = searchTreeQuery(
    metadata?.target_query?.arguments_list
  );
  const sources = sourcesWithDuplicates.filter(
    (value, index, self) => index === self.findIndex((t) => t === value)
  );
  return {
    audience_builder_ids: (audienceBuilders || [])
      .filter((item) => item.slug === metadata?.audience_type)
      .map((item) => item.id),
    source_ids: (fusions || [])
      .filter((item) => sources.indexOf(item.slug) > -1)
      .map((item) => item.id),
  };
};

export const findGroupLevel = (node, groupId, level) => {
  let result = -1;
  if (node.id === groupId) {
    return level;
  }
  if (!isEmpty(node.arguments_list)) {
    node.arguments_list.forEach((n) => {
      const foundLevel = findGroupLevel(n, groupId, level + 1);

      if (foundLevel !== -1) {
        result = foundLevel;
      }
    });
  }

  return result;
};

export const defer = (fn: any, time = 500) => {
  return () => setTimeout(fn, time);
};

export const uniqueByProp = <T extends any>(arr: T[], prop: keyof T) => {
  return arr.filter(
    (value, index, _arr) =>
      _arr.findIndex((value2) => value2[prop] === value[prop]) === index
  );
};

export const numberWithCommas = (number) => {
  if (!number) {
    return false;
  }
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const getAudienceTypeByQuery = (query) => {
  if (!query) {
    return '';
  }
  if (query.query_type === 'conversation') {
    return 'membership';
  }

  return 'interests';
};

export const getSavedQueries = (data, folders) => {
  return [
    ...data.map((item) => {
      const parentFolder = folders.find(
        (folder) =>
          !isEmpty(item.path) && folder.id === `folder_${item.path[0].id}`
      );

      const membershipId = item.metadata && item.metadata.target_membership_id;

      return {
        id:
          item.metadata && item.metadata.query_type === 'conversation'
            ? membershipId
            : `${item.id}`,
        label: item.label,
        source: 'saved',
        taxonomy: [
          (parentFolder && parentFolder.description) || 'Saved queries',
        ],
        type: getAudienceTypeByQuery(item.metadata),
        class: item.metadata && item.metadata.query_type,
        metadata: item.metadata,
      };
    }),
  ];
};

export const getItemPic = (pic, feeds) => {
  let picture = null;
  if (!isEmpty(feeds)) {
    const iosStore = feeds.find((f) => f.feed === 'ios');
    const androidStore = feeds.find((f) => f.feed === 'android');

    picture =
      (iosStore && iosStore.image_url) ||
      (androidStore && androidStore.image_url);
  }
  return picture || pic;
};
